import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FlowPoint {
  label: string;
  income: number;
  expenses: number;
  /** Solde du mois (facultatif) — tracé sur un axe secondaire propre. */
  balance?: number;
}

interface FlowChartProps {
  data: FlowPoint[];
  formatCurrency: (amount: number) => string;
}

const W = 720;
const H = 250;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 18;
const PAD_B = 26;

/**
 * Flux sur N mois — revenus en aire pleine, dépenses en pointillés, et solde
 * (facultatif) en ligne ambre.
 *
 * Dessiné en SVG plutôt qu'en Recharts : dégradé d'aire, grille de 4 filets et
 * points terminaux accentués, tous exprimés en tokens CSS — le graphe suit le
 * thème et le mode clair/sombre sans recalcul JS.
 *
 * Les trois séries partagent la MÊME échelle Y (le solde entre dans le calcul
 * du min/max) : mélanger deux échelles fausserait la lecture (un solde à 2000
 * doit apparaître au-dessus de crédits à 1500).
 *
 * Le contraste des séries est porté par la forme (aire / pointillés / ligne),
 * pas seulement la couleur : lisible aussi en daltonisme. Survol + flèches
 * clavier pour lire chaque mois.
 */
export default function FlowChart({ data, formatCurrency }: FlowChartProps) {
  const gradientId = useId();
  const { t } = useTranslation('dashboard');
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  // Le solde n'est tracé que si TOUS les points le fournissent.
  const hasBalance = data.length >= 2 && data.every((d) => typeof d.balance === 'number');

  const geometry = useMemo(() => {
    if (data.length < 2) return null;

    // Échelle Y UNIQUE et partagée : le solde entre dans le domaine.
    const all = data.flatMap((d) => (hasBalance ? [d.income, d.expenses, d.balance as number] : [d.income, d.expenses]));
    const max = Math.max(...all) * 1.08;
    const min = Math.min(...all, 0) * 0.9;
    const range = max - min || 1;

    const X = (i: number) => PAD_L + (i / (data.length - 1)) * (W - PAD_L - PAD_R);
    const Y = (v: number) => PAD_T + (1 - (v - min) / range) * (H - PAD_T - PAD_B);

    const path = (key: 'income' | 'expenses') =>
      data.map((d, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(d[key]).toFixed(1)}`).join(' ');

    const incomeLine = path('income');
    const balanceLine = hasBalance
      ? data.map((d, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(d.balance as number).toFixed(1)}`).join(' ')
      : '';

    return {
      X,
      Y,
      incomeLine,
      incomeArea: `${incomeLine} L${X(data.length - 1).toFixed(1)} ${H - PAD_B} L${X(0).toFixed(1)} ${H - PAD_B} Z`,
      expenseLine: path('expenses'),
      balanceLine,
      gridLines: [0, 1, 2, 3].map((g) => PAD_T + (g / 3) * (H - PAD_T - PAD_B)),
      lastX: X(data.length - 1),
      lastY: Y(data[data.length - 1].income),
    };
  }, [data, hasBalance]);

  // Index du point le plus proche horizontalement du pointeur.
  const indexFromPointer = useCallback(
    (clientX: number) => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (!rect.width) return null;
      const px = ((clientX - rect.left) / rect.width) * W;
      const ratio = (px - PAD_L) / (W - PAD_L - PAD_R);
      const i = Math.round(ratio * (data.length - 1));
      return Math.max(0, Math.min(data.length - 1, i));
    },
    [data.length],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => setHover(indexFromPointer(e.clientX)),
    [indexFromPointer],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      setHover((prev) => {
        const base = prev ?? data.length - 1;
        const next = e.key === 'ArrowLeft' ? base - 1 : base + 1;
        return Math.max(0, Math.min(data.length - 1, next));
      });
    },
    [data.length],
  );

  if (!geometry) {
    return <p className="px-5 pb-5 text-sm" style={{ color: 'var(--text-3)' }}>{t('noData')}</p>;
  }

  const { X, Y, incomeLine, incomeArea, expenseLine, balanceLine, gridLines, lastX, lastY } = geometry;
  const active = hover !== null ? data[hover] : null;

  const tipBelow = hover !== null && active ? Y(active.income) / H < 0.34 : false;
  const tipX = hover === 0 ? '0' : hover === data.length - 1 ? '-100%' : '-50%';

  return (
    <div className="px-2 pb-3">
      <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto block overflow-visible touch-pan-y"
        style={{ aspectRatio: `${W} / ${H}` }}
        role="img"
        tabIndex={0}
        onBlur={() => setHover(null)}
        onKeyDown={handleKeyDown}
        aria-label={data
          .map((d) => `${d.label}: ${t('common.credits')} ${formatCurrency(d.income)}, ${t('common.totalExpenses')} ${formatCurrency(d.expenses)}${hasBalance ? `, ${t('common.balance')} ${formatCurrency(d.balance as number)}` : ''}`)
          .join(' · ')}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--pa)" stopOpacity=".28" />
            <stop offset="1" stopColor="var(--pa)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridLines.map((y) => (
          <line key={y} x1={PAD_L} x2={W - PAD_R} y1={y.toFixed(1)} y2={y.toFixed(1)} stroke="var(--line)" strokeWidth="1" />
        ))}

        {data.map((d, i) => (
          <text
            key={d.label + i}
            x={X(i).toFixed(1)}
            y={H - 8}
            fill={hover === i ? 'var(--text)' : 'var(--text-3)'}
            fontSize="10.5"
            fontWeight="600"
            textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
          >
            {d.label}
          </text>
        ))}

        <path d={incomeArea} fill={`url(#${gradientId})`} />
        <path d={expenseLine} fill="none" stroke="var(--text-3)" strokeWidth="1.8" strokeDasharray="2 4" strokeLinecap="round" strokeLinejoin="round" />
        {/* Ligne de solde (même échelle) — dessinée sous le trait revenus */}
        {hasBalance && (
          <path d={balanceLine} fill="none" stroke="var(--chart-balance)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".95" />
        )}
        <path d={incomeLine} fill="none" stroke="var(--pa)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />

        {/* Points terminaux — masqués pendant le survol */}
        {hover === null && (
          <>
            {hasBalance && <circle cx={lastX} cy={Y(data[data.length - 1].balance as number)} r="3" fill="var(--chart-balance)" />}
            <circle cx={lastX} cy={lastY} r="6" fill="none" stroke="var(--pa)" strokeWidth="1.5" opacity=".35" />
            <circle cx={lastX} cy={lastY} r="3.4" fill="var(--pa)" />
          </>
        )}

        {/* Guidage de survol */}
        {active && hover !== null && (
          <g pointerEvents="none">
            <line x1={X(hover)} x2={X(hover)} y1={PAD_T} y2={H - PAD_B} stroke="var(--line-2)" strokeWidth="1" />
            <circle cx={X(hover)} cy={Y(active.expenses)} r="3.4" fill="var(--text-3)" />
            {hasBalance && <circle cx={X(hover)} cy={Y(active.balance as number)} r="3.4" fill="var(--chart-balance)" />}
            <circle cx={X(hover)} cy={Y(active.income)} r="6" fill="none" stroke="var(--pa)" strokeWidth="1.5" opacity=".35" />
            <circle cx={X(hover)} cy={Y(active.income)} r="4" fill="var(--pa)" />
          </g>
        )}
      </svg>

      {/* Infobulle — positionnée en % du conteneur, donc insensible au resize */}
      {active && hover !== null && (
        <div
          className="ctip"
          style={{
            left: `${(X(hover) / W) * 100}%`,
            top: `${(Y(active.income) / H) * 100}%`,
            transform: `translate(${tipX}, ${tipBelow ? 'calc(0% + 14px)' : 'calc(-100% - 12px)'})`,
          }}
          aria-hidden
        >
          <div className="r1">{active.label}</div>
          <div className="r2">{t('common.credits')} <b className="num">{formatCurrency(active.income)}</b></div>
          <div className="r3">{t('common.totalExpenses')} <b className="num">{formatCurrency(active.expenses)}</b></div>
          {hasBalance && <div className="r4">{t('common.balance')} <b className="num">{formatCurrency(active.balance as number)}</b></div>}
        </div>
      )}

      {/* Surface unique de captation du pointeur */}
      <div
        className="absolute inset-0 cursor-crosshair"
        aria-hidden
        onPointerMove={handlePointerMove}
        onPointerLeave={() => setHover(null)}
      />
      </div>
    </div>
  );
}
